/**
 * Der Reiter „Bestellung" des Bestell-Drawers - Portierung von `drawerMainTab`
 * (`index.html:8385-8536`). Er haengt sich als `children` in `.drawer-body` des Rahmens aus Task 16.
 *
 * Aufbau, in der Reihenfolge des Originals:
 *
 *   .field.drawer-order-due            Fälligkeitsdatum (AUSSERHALB aller Abschnitte)
 *   sec('main-status',  'Status')      die Statusauswahl
 *   sec('main-data',    'Bestelldaten')  Stammdaten.tsx + Termine.tsx + Notizen
 *   sec('main-items',   'Artikel')     Positionen.tsx
 *   sec('main-arrival', 'Rohstoffkurs')          // bis Task 6: 'Anlieferung & Rohstoffkurs'
 *   sec('main-verlauf', 'Stationsverlauf')  Verlauf.tsx
 *
 * `sec` ist der Port `Abschnitt` aus Task 16; `SEC_COLLAPSED` gilt dort sitzungsweit ueber alle
 * Reiter und startet mit `{ 'main-verlauf': true }` - der Stationsverlauf ist der einzige Abschnitt,
 * der zugeklappt aufgeht (`index.html:4605`).
 *
 * ================= RECHTE: dieser Reiter prueft selbst KEINE =================
 *
 * `drawerMainTab` enthaelt keine einzige Rechtepruefung und kein `data-permission` - die einzige
 * Stelle mit `data-permission` im ganzen `index.html` ist `:8632` im Zahlungs-Reiter. Gesperrt wird
 * der Reiter allein durch den Durchgang in `Drawer.tsx` (`index.html:9296-9304`) ueber
 * `canEditTab = hasPermission(DRAWER_TAB_EDIT_PERMISSIONS['main']) = hasPermission('edit_orders')`.
 *
 * `rechte` steht deshalb in `BestellungTabProps` (der Task-Brief reicht es herein), wird hier aber
 * NICHT gelesen. Wer es hier auswertete, baute eine zweite Rechtequelle neben den Durchgang.
 *
 * ACHTUNG fuer den Nachweis: Kopf und Rumpf haengen in DIESEM Reiter am GLEICHEN Recht
 * (`DrawerKopf` prueft `edit_orders` direkt, `canEditTab['main']` ist ebenfalls `edit_orders`).
 * Die Trennung der beiden Mechanismen laesst sich hier darum nicht zeigen; sie ist im Reiter
 * „Dokumente" belegt (Task 16, `rahmen.test.tsx`).
 *
 * ================= Eine ausgenommene Flaeche =================
 *
 * `Positionen.tsx` traegt Verhaltensaenderung 3 (JTL-Suchfelder, im Ausnahmeregister
 * `test/visual/ausnahmen.ts` eingetragen); alles uebrige in diesem Reiter steht in der Baseline.
 * Im Zustand der Baseline (`best-03`: keine offene Trefferliste) zeichnet sie genau den Baum des
 * Bestands - der Strukturvergleich ist davon unberuehrt.
 *
 * `Termine.tsx` trug bis zum 2026-08-20 zusaetzlich Verhaltensaenderung 5 (DSV-gefuehrte Felder).
 * Die dafuer noetigen acht Felder (Tracking-/Container-Nr., Speditionsportal, ETD (tatsächlich),
 * ETA, vsl. LT 74722 Buchen, Aufschlag auf ETA, Container, Container-Volumen) sind seither in den
 * neuen Reiter „Spedition" umgezogen (`Drawer/ShipTab/`) - der Strukturvergleich dieses Reiters
 * nimmt sie deshalb bewusst aus der Baseline, siehe `test/komponenten/bewussterSpeditionsschnitt.ts`
 * und `docs/superpowers/decisions/2026-08-20-reiter-spedition.md`. `Termine.tsx` fuehrt seither nur
 * noch Bestelldatum und ETD (PI) - beide unveraendert in der Baseline.
 *
 * ================= sendungen/onTracking/onPortal: mit dem Umzug ganz entfallen ==================
 *
 * Mit dem Umzug der acht DSV-Felder in den Reiter „Spedition" haben `Termine.tsx` und
 * `Stammdaten.tsx` keinen Leser mehr fuer `sendungen` bzw. `onTracking`/`onPortal` - keine der drei
 * wird hier noch destrukturiert oder an etwas gereicht. `onTracking` verliess `BestellungTabProps`
 * bereits mit dem Feldumzug ganz (kein Aufrufer reichte es ohnehin herein). `sendungen` und
 * `onPortal` standen dort bis zum Auftrag „Den Reiter ‚Spedition' in App.tsx verdrahten"
 * (2026-08-21) noch GEMELDET, weil `App.tsx` beide weiterhin an diese Komponente reichte - jener
 * Auftrag hat die Uebergabe in `App.tsx` gestrichen (`onPortal` geht seither an `<SpeditionTab>`,
 * `sendungen` faellt ersatzlos weg) und beide Props hier aus `BestellungTabProps` entfernt.
 */
import { useEffect, useRef, useState } from "react";
import { Abschnitt } from "../Abschnitt.tsx";
import { Datumsfeld } from "../Datumsfeld.tsx";
import { orderListDue } from "../../../lib/status.ts";
import { Stammdaten } from "./Stammdaten.tsx";
import { Termine } from "./Termine.tsx";
import { Positionen } from "./Positionen.tsx";
import { Verlauf } from "./Verlauf.tsx";
import { SAMMELSENDUNG_PFLEGEHINWEIS, sperrendeSammelsendung } from "../sammelsendungSperre.ts";
import type { BestellungTabProps } from "./typen.ts";

/** `index.html:8524` - die Auswahl des Rohstoffs, woertlich in dieser Reihenfolge. */
const ROHSTOFFE = ["Kupfer", "Silber", "Aluminium", "Zink", "Nickel", "Messing", "Stahl"] as const;

/** `index.html:8532` - die Waehrungen des Rohstoffkurses, woertlich in dieser Reihenfolge. */
const ROHSTOFF_WAEHRUNGEN = ["USD", "EUR", "CNY", "GBP"] as const;

export type { BestellungTabProps };

export function BestellungTab(props: BestellungTabProps) {
  const {
    bestellung: o,
    lieferanten,
    definitionen,
    onAendern,
    onJtlKopfAendern,
    onStatus,
    artikelBreiten,
    onSpaltenbreiten,
    jtlArtikelSuche,
    onPositionAendern,
    onArtikelTauschen,
    onMengePreisAendern,
    onPositionHinzufuegen,
    onPositionLoeschen,
    positionsDokumenteZustand,
    positionsDokumenteNeuLaden,
    onProduktionsbildOeffnen,
    onStehenderHinweis,
    uploadStellen,
  } = props;

  const id = o.id as string;
  const patch = (feld: string, wert: unknown): void => onAendern(id, { [feld]: wert });

  /**
   * Auftrag C2c, M1: das Notizfeld schreibt seither erst BEIM VERLASSEN des Feldes (`onBlur`), nicht
   * mehr bei jedem Anschlag. Waehrend des Tippens bleibt der Wert lokal in `notizEntwurf` - ansonsten
   * schickten zwei Anschlaege vor der ersten Antwort dieselbe `bRowversion` zweimal: der erste PATCH
   * schreibt, JTL zaehlt die Version hoch, der zweite laeuft in einen 409 - der Nutzer saehe "wurde
   * von einem anderen Benutzer geaendert", ausgeloest vom EIGENEN Tippen. Dazu die Last: jeder
   * Anschlag war bis hierher ein vollstaendiger `spLieferantenBestellungBearbeiten`-Lauf auf einem
   * PRODUKTIVEN Beleg (ein UPDATE ueber 29 Spalten, dazu `spUpdateLagerBestand` und die `tQueue`-
   * Pruefung bei `nStatus > 5`).
   *
   * Das weicht bewusst vom Hausbrauch ab (`onInput` -> sofort schreiben, geerbt vom fruaeheren
   * Overlay-Weg, siehe die uebrigen Textfelder in dieser Datei und in `Stammdaten.tsx`): der
   * Overlay-Weg schrieb in eine Hub-Spalte OHNE Sperre, hier ist das Ziel ein ERP-Beleg MIT
   * optimistischer Sperre - und die bestraft jeden zweiten Anschlag mit einem falschen Konfliktbanner.
   *
   * Der lokale Entwurf zieht ueber diesen Effekt nach, sobald sich der Datensatz VON AUSSEN aendert
   * (anderer Drawer geoeffnet, Bestellung neu geladen) - ohne ihn zeigte das Feld nach einem
   * Bestellungswechsel weiterhin die Notiz der VORIGEN Bestellung, weil `useState` seinen Anfangswert
   * nur beim allerersten Zeichnen uebernimmt.
   *
   * RICHTIGSTELLUNG (Auftrag G, 2026-08-22): hier stand bis dahin, `o.internerKommentar` aendere sich
   * waehrend des Tippens ja gerade NICHT, der Effekt greife also nie in eine laufende Eingabe ein -
   * das ist FALSCH (die neunte Behauptung dieser Reihe, die der Messung nicht standhaelt).
   * `o.internerKommentar` aendert sich sehr wohl mitten in einer laufenden Eingabe: naemlich dann,
   * wenn die ANTWORT auf einen FRUEHEREN PATCH eintrifft, waehrend der Nutzer laengst weitergetippt
   * hat - "Feld verlassen, sofort wieder hinein und nachgebessert" (derselbe Fall, den `App.tsx` an
   * anderer Stelle ausdruecklich als real benennt). Nachgestellt: Nutzer tippt "A", verlaesst das
   * Feld (PATCH #1 geht raus, Antwort steht noch aus), tippt sofort weiter zu "AB" - dann trifft die
   * Antwort auf PATCH #1 ein und setzt `o.internerKommentar` auf "A". Ohne Schutz ueberschreibt der
   * Effekt den nachgetippten Text (`Expected AB / Received A`) - kein Datenverlust in JTL, aber
   * Verlust von Nutzereingabe, mit der Antwortzeit eines PATCH auf einem produktiven ERP-Beleg als
   * Zeitfenster.
   *
   * Der Effekt unterscheidet deshalb zwei Faelle ueber `zuletztServerWertRef` (der Server-Stand, auf
   * dem der aktuelle Entwurf zuletzt beruhte):
   *   - Bestellungswechsel (`id` aendert sich): der Entwurf wird IMMER auf den neuen Serverwert
   *     zurueckgesetzt - ein angefangener, nicht abgeschickter Entwurf der VORIGEN Bestellung gehoert
   *     nicht in die neue (unveraendertes Verhalten, eigener Test).
   *   - gleiche Bestellung, nur `o.internerKommentar` aendert sich (PATCH-Antwort auf die eigene
   *     Eingabe ODER eine echte Fremdaenderung von aussen/SSE): der neue Wert wird nur uebernommen,
   *     wenn der Entwurf noch mit dem zuletzt bekannten Serverwert uebereinstimmt, also KEIN
   *     abweichender Entwurf offen ist. Hat der Nutzer seither weitergetippt, bleibt der Entwurf
   *     unangetastet - genau das schliesst die Luecke oben, ohne den Fall "echte Fremdaenderung ohne
   *     offenen Entwurf" zu verlieren (dort stimmt der Entwurf noch mit dem letzten Serverwert
   *     ueberein, der neue wird also brav uebernommen).
   */
  const [notizEntwurf, setNotizEntwurf] = useState<string>((o.internerKommentar as string) ?? "");
  const vorigeIdRef = useRef(id);
  const zuletztServerWertRef = useRef<string>((o.internerKommentar as string) ?? "");
  useEffect(() => {
    const serverWert = (o.internerKommentar as string) ?? "";
    const bestellungsWechsel = vorigeIdRef.current !== id;
    const bekannterServerWert = zuletztServerWertRef.current;
    vorigeIdRef.current = id;
    zuletztServerWertRef.current = serverWert;
    if (bestellungsWechsel) {
      setNotizEntwurf(serverWert);
      return;
    }
    setNotizEntwurf((bisher) => (bisher === bekannterServerWert ? serverWert : bisher));
  }, [id, o.internerKommentar]);

  /** Siehe Kopfkommentar `notizEntwurf` oben - schreibt nur, wenn sich der Entwurf vom JTL-Stand unterscheidet. */
  const notizVerlassen = (): void => {
    const bisher = (o.internerKommentar as string) ?? "";
    if (notizEntwurf === bisher) return;
    onJtlKopfAendern?.(id, { cInternerKommentar: notizEntwurf });
  };

  // `index.html:8390-8393` kennt keinen leeren Eintrag; passt `o.status` auf keine Spalte, traegt
  // kein `<option>` das `selected` und der Browser stellt die ERSTE dar. Dieselbe Nachbildung wie
  // beim Container in `Termine.tsx` - Begruendung dort.
  const statusListe = definitionen.STATUSES;
  const angezeigterStatus = statusListe.some((s) => s.id === o.status)
    ? (o.status as string)
    : ((statusListe[0]?.id as string) ?? "");

  return (
    <>
      <div className="field drawer-order-due">
        <label>Fälligkeitsdatum</label>
        <Datumsfeld wert={orderListDue(o)} onCommit={(wert) => patch("listDueDate", wert)} />
      </div>

      <Abschnitt schluessel="main-status" titel="Status">
        <div className="field-grid">
          <div className="field wide">
            {/* `moveOrder(o.id, this.value)` (index.html:7764) - schreibt zusaetzlich den Verlauf. */}
            <select
              value={angezeigterStatus}
              // Task 7: der Status ist ein geteiltes Kopffeld der Sammelsendung.
              disabled={sperrendeSammelsendung(o) !== null}
              title={sperrendeSammelsendung(o) !== null ? SAMMELSENDUNG_PFLEGEHINWEIS : undefined}
              onChange={(e) => onStatus?.(id, e.currentTarget.value)}
            >
              {statusListe.map((s) => (
                <option key={s.id as string} value={s.id as string}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Abschnitt>

      <Abschnitt schluessel="main-data" titel="Bestelldaten">
        <div className="field-grid">
          <Stammdaten
            bestellung={o}
            lieferanten={lieferanten}
            onAendern={onAendern}
            onJtlKopfAendern={onJtlKopfAendern}
          />
          <Termine bestellung={o} onAendern={onAendern} />
          <div className="field wide">
            <label>Notizen</label>
            {/*
             * `index.html:8475`. ANMERKUNG zur DOM-Form: der Bestand schreibt den Text als KIND des
             * `<textarea>`, React setzt ihn als `value`-Eigenschaft ohne Textknoten. Sichtbar ist
             * beides gleich; in einer DOM-Signatur unterscheidet sich der `text` des `textarea`,
             * sobald Notizen vorhanden sind. In der Baseline (`best-03`) sind sie leer.
             *
             * Auftrag C2b: Wert UND Schreibweg kommen seither aus JTL (`internerKommentar`/
             * `cInternerKommentar`), nicht mehr aus dem Hub-Overlay (`o.notes`/`patch("notes", …)`) -
             * siehe Kopfkommentar `api/jtlBestellungen.ts`, "WELCHE SEITE GEWINNT". Mehrzeiliger
             * Freitext: `e.currentTarget.value` traegt Zeilenumbrueche unveraendert (`\n`), JTLs
             * `cInternerKommentar nvarchar(max)` schraenkt sie nicht ein - keine eigene Behandlung
             * noetig.
             *
             * Auftrag C2c, M1: gezeichnet wird der LOKALE Entwurf (`notizEntwurf`), nicht mehr
             * `o.internerKommentar` direkt - `onInput` haelt ihn waehrend des Tippens nach, geschrieben
             * wird erst bei `onBlur` (`notizVerlassen`, Kopfkommentar oben an der Komponente).
             */}
            <textarea
              placeholder="Besonderheiten…"
              value={notizEntwurf}
              onInput={(e) => setNotizEntwurf(e.currentTarget.value)}
              onChange={() => {}}
              onBlur={notizVerlassen}
            />
          </div>
        </div>
      </Abschnitt>

      <Abschnitt schluessel="main-items" titel="Artikel">
        <Positionen
          bestellung={o}
          lieferanten={lieferanten}
          onAendern={onAendern}
          artikelBreiten={artikelBreiten}
          onSpaltenbreiten={onSpaltenbreiten}
          jtlArtikelSuche={jtlArtikelSuche}
          onPositionAendern={onPositionAendern}
          onArtikelTauschen={onArtikelTauschen}
          onMengePreisAendern={onMengePreisAendern}
          onPositionHinzufuegen={onPositionHinzufuegen}
          onPositionLoeschen={onPositionLoeschen}
          positionsDokumenteZustand={positionsDokumenteZustand}
          positionsDokumenteNeuLaden={positionsDokumenteNeuLaden}
          onProduktionsbildOeffnen={onProduktionsbildOeffnen}
          onStehenderHinweis={onStehenderHinweis}
          uploadStellen={uploadStellen}
        />
      </Abschnitt>

      <Abschnitt schluessel="main-arrival" titel="Rohstoffkurs">
        <div className="field-grid">
          <div className="field">
            <label>Rohstoff</label>
            <select
              value={(o.rawMaterial as string) || ""}
              onChange={(e) => patch("rawMaterial", e.currentTarget.value)}
            >
              <option value="">— auswählen —</option>
              {ROHSTOFFE.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Rohstoffkurs</label>
            <input
              className="mono"
              type="number"
              min="0"
              step="0.0001"
              value={(o.rawMaterialRate as string) ?? ""}
              placeholder="0,0000"
              onChange={(e) => patch("rawMaterialRate", e.currentTarget.value)}
            />
          </div>
          <div className="field">
            <label>Währung</label>
            <select
              value={(o.rawMaterialCurrency as string) || "USD"}
              onChange={(e) => patch("rawMaterialCurrency", e.currentTarget.value)}
            >
              {ROHSTOFF_WAEHRUNGEN.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Abschnitt>

      <Abschnitt schluessel="main-verlauf" titel="Stationsverlauf">
        <Verlauf bestellung={o} definitionen={definitionen} />
      </Abschnitt>
    </>
  );
}
